import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSnackbar } from 'notistack';
import { useApiQuery } from '../../../../../api/hooks';
import apiClient from '../../../../../api/apiClient';
import { usePreloaderStore } from '../../../../../stores';
import { PersonFillIcon } from '../../../model/icons';
import GameHeader from '../GameHeader';
import KillerStepper from './KillerStepper';
import './killer.css';

const BASE = '/api/killer/admin/killer';

// Игра «Киллер»: степпер генерации (список → фото → генерация → скачать) + сетка участников.
export default function KillerGame({ shift }) {
    const smenaId = shift.id;
    const { data: killer, isLoading, error } = useApiQuery(
        ['killer', 'games', smenaId],
        `${BASE}/find-by-smena?smenaId=${smenaId}`,
        { refetchOnMount: 'always', enabled: !!smenaId }
    );

    if (error) return <div className="vp-empty">{error.response?.data?.message || 'Ошибка загрузки игры «Киллер»'}</div>;
    if (isLoading) return <div className="vp-empty">Загрузка…</div>;
    if (!killer) return null;

    return <KillerContent killer={killer} shift={shift} />;
}

KillerGame.propTypes = {
    shift: PropTypes.shape({ id: PropTypes.number.isRequired, title: PropTypes.string }).isRequired
};

function KillerContent({ killer, shift }) {
    const { enqueueSnackbar } = useSnackbar();
    const { show: showPreloader, hide: hidePreloader } = usePreloaderStore();

    const [playerSet, setPlayerSet] = useState(killer.playerSet || []);
    const [withNames, setWithNames] = useState([]);
    const [withAvatars, setWithAvatars] = useState([]);

    const playerSetView = (() => {
        if (playerSet.length > 0) return [...playerSet].sort((a, b) => a.orderId - b.orderId);
        if (withNames.length > 0 && withAvatars.length > 0) {
            return withNames.map(p => {
                const avatar = withAvatars.find(a => a.uniqueId === p.uniqueId);
                return avatar ? { ...p, mediaFile: avatar.mediaFile } : p;
            });
        }
        if (withNames.length > 0) return withNames;
        return [];
    })();

    const status = playerSet.length > 0 ? 4 : (withNames.length > 0 && withAvatars.length > 0) ? 3 : withNames.length > 0 ? 2 : 1;

    const pickFile = (accept, multiple, onPick) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.multiple = multiple;
        input.style.display = 'none';
        input.onchange = (ev) => onPick(ev.target.files);
        document.body.appendChild(input);
        input.click();
        input.remove();
    };

    const handleNamesUpload = () => pickFile('.txt', false, async (files) => {
        if (!files.length) return;
        showPreloader();
        try {
            const fd = new FormData();
            fd.append('file', files[0]);
            const response = await apiClient.post(`${BASE}/upload-names`, fd);
            setWithNames(response.data);
            enqueueSnackbar('Список участников загружен', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Ошибка при загрузке файла', { variant: 'error' });
        } finally { hidePreloader(); }
    });

    const handlePhotoUpload = () => pickFile('image/*', true, async (files) => {
        if (!files.length) return;
        showPreloader();
        try {
            const fd = new FormData();
            Array.from(files).forEach(file => {
                const uniqueId = parseInt(file.name.split('.')[0], 10);
                if (!Number.isNaN(uniqueId)) { fd.append('files', file); fd.append('uniqueIds', uniqueId); }
            });
            const response = await apiClient.post(`${BASE}/${killer.id}/upload-media`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setWithAvatars(response.data);
            enqueueSnackbar('Фотографии загружены', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Ошибка при загрузке фотографий', { variant: 'error' });
        } finally { hidePreloader(); }
    });

    const handleGenerate = async () => {
        showPreloader();
        try {
            const response = await apiClient.post(`${BASE}/${killer.id}/generate-sequence`, playerSetView);
            setPlayerSet(response.data);
            enqueueSnackbar('Последовательность сгенерирована', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Ошибка при генерации', { variant: 'error' });
        } finally { hidePreloader(); }
    };

    const handleDownload = async () => {
        showPreloader();
        try {
            const response = await apiClient.get(`${BASE}/${killer.id}/download`, { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = `killer_${killer.id}.zip`;
            link.click();
            URL.revokeObjectURL(url);
            enqueueSnackbar('Скачивание началось', { variant: 'success' });
        } catch (e) {
            enqueueSnackbar('Ошибка при скачивании', { variant: 'error' });
        } finally { hidePreloader(); }
    };

    const handleDelete = async () => {
        showPreloader();
        try {
            await apiClient.delete(`${BASE}/${killer.id}/delete-sequence`);
            enqueueSnackbar('Последовательность удалена', { variant: 'success' });
            setPlayerSet([]); setWithAvatars([]); setWithNames([]);
        } catch (e) {
            enqueueSnackbar('Ошибка при удалении', { variant: 'error' });
        } finally { hidePreloader(); }
    };

    const stepHandlers = [handleNamesUpload, handlePhotoUpload, handleGenerate, handleDownload];

    return (
        <>
            <GameHeader
                title={`Киллер — ${shift.title || ''}`}
                meta={<span><b>{playerSetView.length}</b> участников</span>}
            />
            <section className="content white">
                <KillerStepper status={status} onStep={(i) => stepHandlers[i]()} />

                {playerSetView.length > 0 ? (
                    <div className="kgrid">
                        {playerSetView.map((player, index) => (
                            <div key={player.id || player.uniqueId || index}>
                                <div className="kphoto">
                                    {player.mediaFile?.mediaFileUrl
                                        ? <img src={player.mediaFile.mediaFileUrl} alt="" />
                                        : <PersonFillIcon />}
                                </div>
                                <div className="kname">{player.orderId ? `${player.orderId}. ` : ''}{player.name}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="kempty">Нет участников</div>
                )}

                {status === 4 && (
                    <div className="kdel">
                        <button type="button" className="btn btn-danger" onClick={handleDelete}>Удалить последовательность</button>
                    </div>
                )}
            </section>
        </>
    );
}

KillerContent.propTypes = {
    killer: PropTypes.shape({ id: PropTypes.number, playerSet: PropTypes.array }).isRequired,
    shift: PropTypes.shape({ title: PropTypes.string }).isRequired
};
